import { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import Layout from '@/components/Layout';
import { API } from '@/App';
import { 
  CheckCircle2, XCircle, Clock, AlertCircle, 
  Package, Calendar, IndianRupee, MessageSquare
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { 
  Dialog, DialogContent, DialogHeader, DialogTitle, 
  DialogFooter, DialogDescription 
} from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { formatDate } from '@/utils/dateFormat';

/**
 * CLIENT APPROVALS PAGE
 * 
 * Shows tasks that require client approval.
 * Client can approve or reject with comments.
 */
export default function ClientApprovals() {
  const [pendingTasks, setPendingTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [actionDialogOpen, setActionDialogOpen] = useState(false);
  const [selectedTask, setSelectedTask] = useState(null);
  const [actionType, setActionType] = useState('approve'); // 'approve' or 'reject'
  const [comment, setComment] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    fetchPendingApprovals();
  }, []);

  const fetchPendingApprovals = async () => {
    try {
      const response = await axios.get(`${API}/client/pending-approvals`);
      setPendingTasks(response.data);
    } catch (error) {
      console.error('Failed to fetch pending approvals:', error);
      toast.error('Failed to load pending approvals');
    } finally {
      setLoading(false);
    }
  };

  const openActionDialog = (task, action) => {
    setSelectedTask(task);
    setActionType(action);
    setComment('');
    setActionDialogOpen(true);
  };

  const handleApprovalAction = async () => {
    if (!selectedTask) return;
    
    setSubmitting(true);
    try {
      await axios.put(`${API}/client/tasks/${selectedTask.id}/approve`, {
        approved: actionType === 'approve',
        comment: comment.trim() || null
      });
      
      toast.success(actionType === 'approve' ? 'Task approved!' : 'Task rejected');
      setActionDialogOpen(false);
      setSelectedTask(null);
      setComment('');
      fetchPendingApprovals();
    } catch (error) {
      toast.error(error.response?.data?.detail || `Failed to ${actionType} task`);
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-96">
          <div className="text-stone-400">Loading...</div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-6" data-testid="client-approvals">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-stone-800">Pending Approvals</h1>
            <p className="text-stone-500 mt-1">
              Tasks requiring your approval before proceeding
            </p>
          </div>
          <Badge className="bg-amber-100 text-amber-700 text-lg px-4 py-2">
            {pendingTasks.length} Pending
          </Badge>
        </div>

        {/* Empty State */}
        {pendingTasks.length === 0 ? (
          <div className="apple-card p-12 text-center">
            <CheckCircle2 size={48} className="mx-auto text-green-400 mb-4" />
            <h3 className="text-lg font-medium text-stone-600 mb-2">All Caught Up!</h3>
            <p className="text-stone-400">
              You have no tasks pending approval.
            </p>
          </div>
        ) : (
          <div className="space-y-4">
            {pendingTasks.map((task) => (
              <div 
                key={task.id} 
                className="apple-card p-6 border-l-4 border-amber-400"
                data-testid={`pending-task-${task.id}`}
              >
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <Clock size={20} className="text-amber-500" />
                      <h3 className="font-semibold text-stone-800">{task.title}</h3>
                      <Badge className="bg-amber-100 text-amber-700">
                        Pending Approval
                      </Badge>
                    </div>
                    
                    <div className="flex items-center gap-4 text-sm text-stone-500 mb-3">
                      <span className="flex items-center gap-1">
                        <Package size={14} />
                        {task.project_name}
                      </span>
                      {task.category_name && (
                        <span className="px-2 py-0.5 bg-stone-100 rounded">
                          {task.category_name}
                        </span>
                      )}
                      <span className="flex items-center gap-1">
                        <Calendar size={14} />
                        Requested {formatDate(new Date(task.approval_requested_at))}
                      </span>
                    </div>
                    
                    {task.description && (
                      <p className="text-sm text-stone-600 mb-3">{task.description}</p>
                    )}
                    
                    {task.approval_message && (
                      <div className="bg-blue-50 rounded-lg p-3 mb-3">
                        <div className="flex items-start gap-2">
                          <MessageSquare size={16} className="text-blue-500 mt-0.5" />
                          <div>
                            <p className="text-xs text-blue-600 font-medium mb-1">Admin Message</p>
                            <p className="text-sm text-blue-800">{task.approval_message}</p>
                          </div>
                        </div>
                      </div>
                    )}
                    
                    <div className="flex items-center gap-6 text-sm">
                      <span className="text-stone-500">
                        {task.quantity} {task.unit}
                      </span>
                      <span className="font-semibold text-blue-600">
                        ₹{task.total?.toLocaleString()}
                      </span>
                      {task.progress_percentage > 0 && (
                        <span className="text-stone-400">
                          {task.progress_percentage}% complete
                        </span>
                      )}
                    </div>
                  </div>
                  
                  <div className="flex flex-col gap-2">
                    <Button
                      onClick={() => openActionDialog(task, 'approve')}
                      className="bg-green-600 hover:bg-green-700 text-white"
                      data-testid={`approve-task-${task.id}`}
                    >
                      <CheckCircle2 size={16} className="mr-2" />
                      Approve
                    </Button>
                    <Button
                      variant="outline"
                      onClick={() => openActionDialog(task, 'reject')}
                      className="border-red-300 text-red-600 hover:bg-red-50"
                      data-testid={`reject-task-${task.id}`}
                    >
                      <XCircle size={16} className="mr-2" />
                      Reject
                    </Button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Action Dialog */}
        <Dialog open={actionDialogOpen} onOpenChange={setActionDialogOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle className={`flex items-center gap-2 ${actionType === 'approve' ? 'text-green-600' : 'text-red-600'}`}>
                {actionType === 'approve' ? (
                  <CheckCircle2 size={20} />
                ) : (
                  <XCircle size={20} />
                )}
                {actionType === 'approve' ? 'Approve Task' : 'Reject Task'}
              </DialogTitle>
              <DialogDescription>
                {actionType === 'approve' 
                  ? `Are you sure you want to approve "${selectedTask?.title}"?`
                  : `Are you sure you want to reject "${selectedTask?.title}"? The team will be notified.`
                }
              </DialogDescription>
            </DialogHeader>
            
            <div className="py-4">
              <Label>Comment {actionType === 'reject' && '(Recommended)'}</Label>
              <Textarea
                value={comment}
                onChange={(e) => setComment(e.target.value)}
                placeholder={actionType === 'approve' 
                  ? 'Optional: Add any notes...' 
                  : 'Please explain why you are rejecting this task...'
                }
                className="mt-2"
                data-testid="approval-comment"
              />
            </div>
            
            <DialogFooter>
              <Button 
                variant="outline" 
                onClick={() => setActionDialogOpen(false)}
              >
                Cancel
              </Button>
              <Button
                onClick={handleApprovalAction}
                disabled={submitting}
                className={actionType === 'approve' 
                  ? 'bg-green-600 hover:bg-green-700 text-white' 
                  : 'bg-red-600 hover:bg-red-700 text-white'
                }
              >
                {submitting 
                  ? 'Submitting...' 
                  : actionType === 'approve' ? 'Confirm Approval' : 'Confirm Rejection'
                }
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
}
